import React from 'react'
import { Box, Stack, Fade, Typography } from '@mui/material'
import LazyLoad from 'react-lazyload'
import { FormattedMessage } from 'react-intl'
import useResponsiveImageWidth from '../../customHooks/responsiveImageWidth'

interface IBlockFullMain {
  image?: string
  altImage?: string
  title: string
  subtitle?: string[]
  titleColor?: string
  iconImage?: string
  altIcon?: string
  description?: string[]
  shadow?: boolean
  color?: string
  videoMP4?: string
  children?: React.ReactNode
  bgColor?: string
  section?: boolean
  iconImageSx?: object
}

function styles(props: IBlockFullMain) {
  return {
    box: {
      background: props.bgColor ?? '#111',
      px: { xs: 2, sm: 8, lg: 12 },
      py: { xs: 6, sm: 8, lg: 12 }
    },
    boxWidth: {
      maxWidth: 1760,
      marginInline: 'auto'
    },
    container: {
      overflow: 'hidden',
      gap: { xs: 4, sm: 8 },
      position: 'relative',
      justifyContent: 'center',
      minHeight: { xs: 0, lg: 600 }
    },
    imgContainer: {
      display: 'flex',
      borderRadius: 3,
      overflow: 'hidden',
      position: { xs: 'relative', lg: 'absolute' },
      width: { xs: '100%', lg: '70%', xl: '75%' },
      right: 0,
      height: '100%'
    },
    img: {
      height: '100%',
      width: '100%',
      objectFit: 'cover'
    },
    textContainer: {
      textAlign: 'left',
      alignItems: 'flex-start',
      gap: 2,
      maxWidth: { xs: '100%', lg: 520, xl: 600 },
      zIndex: 1,
      position: 'relative'
    },
    iconImage: {
      height: { xs: 12, sm: 14 }
    },
    title: {
      color: props.titleColor,
      fontWeight: 800,
      mb: { xs: 1, sm: 2 },
      fontSize: { xs: '2.5rem', sm: '3.5rem', md: '5rem' }
    },
    subtitle: {
      fontSize: '1.2rem',
      fontWeight: 500,
      color: props.titleColor
    },
    description: {
      fontSize: '1.2rem',
      fontWeight: 500,
      color: props.color
    },
    children: {
      flexDirection: 'row',
      gap: 2,
      mt: { xs: 2, sm: 3 },
      flexWrap: 'wrap'
    },
    shadow: {
      background: props.bgColor ?? '#111',
      opacity: 0.7,
      height: 400,
      width: 400,
      position: 'absolute',
      borderRadius: 200,
      right: 0,
      filter: 'blur(100px)',
      zIndex: -1,
      display: { xs: 'none', lg: 'flex' }
    }
  }
}

const BlockFullMain = (props: IBlockFullMain) => {
  const responsiveImageWidth = useResponsiveImageWidth()
  const {
    container,
    imgContainer,
    img,
    textContainer,
    iconImage,
    title,
    description,
    children,
    box,
    boxWidth,
    subtitle,
    shadow
  } = styles(props)
  return (
    <Box component={props.section ? 'section' : 'div'} sx={{ ...box }}>
      <Box sx={{ ...boxWidth }}>
        <LazyLoad height={600} offset={200}>
          <Fade in={true} timeout={1000}>
            <Stack sx={{ ...container }}>
              <Stack sx={{ ...textContainer }}>
                {props.iconImage && (
                  <Box
                    component='img'
                    sx={props.iconImageSx ?? { ...iconImage }}
                    src={props.iconImage}
                    alt={props.altIcon}
                  />
                )}
                {props.title && (
                  <Typography component='h1' variant='h1' sx={{ ...title }}>
                    <FormattedMessage id={props.title} />
                  </Typography>
                )}
                {props.subtitle &&
                  props.subtitle.map((item: string, index: number) => (
                    <Typography sx={{ ...subtitle }} key={index}>
                      <FormattedMessage id={item} />
                    </Typography>
                  ))}
                {props.description &&
                  props.description.map((item: string, index: number) => (
                    <Typography sx={{ ...description }} key={index}>
                      <FormattedMessage id={item} />
                    </Typography>
                  ))}
                {props.children && <Stack sx={{ ...children }}>{props.children}</Stack>}
                {props.shadow && <Box sx={{ ...shadow }} />}
              </Stack>
              <Box sx={{ ...imgContainer }}>
                {props.videoMP4 && (
                  <Box
                    component='video'
                    src={props.videoMP4}
                    typeof='video/mp4'
                    autoPlay
                    loop
                    muted
                    sx={{ ...img, position: 'absolute' }}
                  />
                )}
                {props.image && (
                  <Box
                    component='img'
                    sx={{ ...img }}
                    src={`${props.image}?wid=${responsiveImageWidth}`}
                    alt={props.altImage}
                  />
                )}
              </Box>
            </Stack>
          </Fade>
        </LazyLoad>
      </Box>
    </Box>
  )
}
export default BlockFullMain
